import React, {useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {useForm} from "react-hook-form";
import {useAcceptJS} from "../shared/UseAcceptJS";
import {publicCustomerService} from "../services/publicCustomerService";
import notificationService from "../services/NotificationService";
import {authorize_net_client_key, authorize_net_login_id} from "../services/common.services";
import {formatCardNumber, validateCardNumber, getCardType, getBusinessOrCustomerName} from "../shared/sharedFunctions";
import CustomInput from "../shared/components/CustomInput";
import FormSelectCustom from "../shared/components/FormSelectCustom";
import {allStates} from "../shared/StatesDropdown";
import "../styles/css/admin-style.css";

const MakePaymentPage: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const customerData = (location.state as any)?.customerData;

    const {register, trigger, getValues, setValue, formState: {errors}} = useForm();
    const {loaded, error, accept} = useAcceptJS();

    const [selectedJobIndex, setSelectedJobIndex] = useState(0);
    const [useNewCard, setUseNewCard] = useState(!customerData?.hasSavedPaymentMethod);
    const [saveCard, setSaveCard] = useState(true);
    const [billingAddressSameAsProperty, setBillingAddressSameAsProperty] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [remainingJobs, setRemainingJobs] = useState(customerData?.outstandingJobs || []);
    const [paidJobs, setPaidJobs] = useState<Array<{ jobId: number; amountCharged: number; transactionId: string }>>([]);

    if (!customerData) {
        return (
            <div className="view-estimate-container notEstimate">
                <div className="estimate-form">
                    <div className="mb-4">
                        <img src="/logo/logoBigger.svg" alt="Barreca Blacktop Sealcoating, Inc" className="logo"/>
                    </div>
                    <div className="card p-4 mb-3 text-center">
                        <h3 className="mb-3">No payment information available</h3>
                        <p>Please return to the login page and verify your customer information.</p>
                        <button onClick={() => navigate('/login')} className="btn btn-primary btn-lg">Back to Login</button>
                    </div>
                </div>
            </div>
        );
    }

    const selectedJob = remainingJobs[selectedJobIndex];
    const customerName = getBusinessOrCustomerName(customerData);
    const propertyAddress = customerData.standardizedAddress ||
        `${customerData.streetNumber || ''} ${customerData.streetName || ''}, ${customerData.city || ''}, ${customerData.state || ''} ${customerData.postalCode || ''}`.trim();

    const handlePaymentSuccess = (response: any) => {
        const justPaid = remainingJobs[selectedJobIndex];
        const newPaidJobs = [...paidJobs, {
            jobId: justPaid.jobId,
            amountCharged: response.amountCharged ?? justPaid.amountDue,
            transactionId: response.transactionId ?? '',
        }];

        const newRemaining = remainingJobs.filter((_: any, i: number) => i !== selectedJobIndex);

        if (newRemaining.length === 0) {
            setTimeout(() => {
                navigate('/make-payment/thanks', {
                    state: { customerName, paidJobs: newPaidJobs },
                    replace: true
                });
            }, 0);
        } else {
            setSubmitting(false);
            setPaidJobs(newPaidJobs);
            setRemainingJobs(newRemaining);
            setSelectedJobIndex(0);
            notificationService.success(
                `Payment of $${justPaid.amountDue.toFixed(2)} for Job #${justPaid.jobId} was successful!`
            );
        }
    };

    const handleExpDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let val = e.target.value.replace(/\D/g, '').slice(0, 4);
        if (val.length >= 3) val = val.slice(0, 2) + '/' + val.slice(2);
        setValue("cardExp", val);
    };

    function onTokenReceived(dataValue: string, dataDescriptor: string) {
        const all = getValues();
        const parts = all.cardExp.split('/');
        if (parts.length !== 2) {
            notificationService.error("Invalid expiration format. Expected MM/YY.");
            setSubmitting(false);
            return;
        }
        const [month, year] = parts;
        let normalizedYear = year;
        if (year.length === 2) {
            normalizedYear = `20${year}`;
        }

        const billingAddress = billingAddressSameAsProperty
            ? propertyAddress
            : all.billingAddress;
        const billingCity = billingAddressSameAsProperty
            ? (customerData.city || '')
            : all.billingCity;
        const billingState = billingAddressSameAsProperty
            ? (customerData.state || '')
            : all.billingState;
        const billingPostalCode = billingAddressSameAsProperty
            ? (customerData.postalCode || '')
            : all.billingPostalCode;

        const postObject = {
            customerNumber: customerData.customerNumber,
            jobId: selectedJob.jobId,
            useSavedPaymentMethod: false,
            saveCard: saveCard,
            paymentTokenDescriptor: dataDescriptor,
            paymentToken: dataValue,
            cardLast4: all.cardNumber.replace(/\s+/g, "").slice(-4),
            cardType: getCardType(all.cardNumber),
            cardExpirationMonth: parseInt(month),
            cardExpirationYear: parseInt(normalizedYear),
            billingName: all.cardName,
            billingAddress: billingAddress,
            billingCity: billingCity,
            billingState: billingState,
            billingPostalCode: billingPostalCode,
        };

        publicCustomerService.makePayment(postObject).then(r => {
            if (r && r.succeeded) {
                handlePaymentSuccess(r);
            } else {
                setSubmitting(false);
                notificationService.error(r?.errorMessage || "Payment failed. Please try again.");
            }
        }).catch(() => {
            setSubmitting(false);
        });
    }

    const handleSubmit = async () => {
        if (submitting) return;

        if (!useNewCard) {
            setSubmitting(true);
            const postObject = {
                customerNumber: customerData.customerNumber,
                jobId: selectedJob.jobId,
                useSavedPaymentMethod: true,
            };
            try {
                const r = await publicCustomerService.makePayment(postObject);
                if (r && r.succeeded) {
                    handlePaymentSuccess(r);
                } else {
                    setSubmitting(false);
                    notificationService.error(r?.errorMessage || "Payment failed. Please try again.");
                }
            } catch {
                setSubmitting(false);
            }
            return;
        }

        if (!loaded) {
            notificationService.error("Payment library still loading. Please wait.");
            return;
        }
        if (error) {
            notificationService.error("Payment library failed to load.");
            return;
        }
        if (!accept) {
            notificationService.error("Payment processing is not available.");
            return;
        }

        const validation = await trigger();
        if (!validation) {
            notificationService.warning("Please fill in all required fields.");
            return;
        }

        const all = getValues();
        const cardNumber = all.cardNumber.replace(/\s+/g, "");
        const cardCode = all.cardSecurity.replace(/\s+/g, "");
        const name = all.cardName.trim();
        const zip = billingAddressSameAsProperty
            ? (customerData.postalCode || '')
            : (all.billingPostalCode || '').trim();

        const parts = all.cardExp.split('/');
        if (parts.length !== 2) {
            notificationService.error("Invalid expiration format. Expected MM/YY.");
            return;
        }
        const [month, year] = parts;
        let normalizedYear = year;
        if (year.length === 2) {
            normalizedYear = `20${year}`;
        }

        setSubmitting(true);

        const authData = {
            clientKey: authorize_net_client_key,
            apiLoginID: authorize_net_login_id,
        };
        const cardData = {cardNumber, month, year: normalizedYear, cardCode};
        const billing = {fullName: name, zip};
        const secureData = {authData, cardData, billing};

        const callback = (response: any) => {
            if (response.messages.resultCode === "Error") {
                const msg = response.messages.message[0];
                notificationService.error(msg.text);
                setSubmitting(false);
            } else {
                const {dataValue, dataDescriptor} = response.opaqueData;
                onTokenReceived(dataValue, dataDescriptor);
            }
        };

        accept.dispatchData(secureData, callback);
    };

    return (
        <div className="view-estimate-container notEstimate">
                    <div className="estimate-form">
                        <div className="mb-4">
                            <img src="/logo/logoBigger.svg" alt="Barreca Blacktop Sealcoating, Inc" className="logo"/>
                        </div>

                        {paidJobs.length > 0 &&
                            <div className="alert alert-success d-flex align-items-center mb-3" role="alert">
                                <i className="fas fa-circle-check me-2"></i>
                                <span><strong>{paidJobs.length} invoice{paidJobs.length > 1 ? 's' : ''}</strong> paid so far. {remainingJobs.length} remaining.</span>
                            </div>
                        }

                        {remainingJobs.length > 1 &&
                            <div className="moarEstimates">
                                <div className="invoiceSelector mb-4">
                                    <div className="alert alert-warning d-flex align-items-center mb-3" role="alert">
                                        <i className="far fa-exclamation-triangle me-2"></i>
                                        <span>You have <strong>{remainingJobs.length} invoices</strong> requiring payment. Select one below to review and pay.</span>
                                    </div>
                                    <div className="row g-3">
                                        {remainingJobs.map((job, index) => (
                                            <div className="col-md-6" key={job.jobId}>
                                                <div
                                                    className={`card noticeMe${index === selectedJobIndex ? ' shadow-sm active' : ''}`}
                                                    style={{cursor: 'pointer'}}
                                                    onClick={() => setSelectedJobIndex(index)}
                                                >
                                                    <div className="card-body d-flex justify-content-between align-items-center">
                                                        <div>
                                                            <h6 className="mb-1 fw-bold">Job #{job.jobId}</h6>
                                                            <small className="text-muted">Completed: {new Date(job.dateDone).toLocaleDateString('en-US', {timeZone: 'UTC'})}</small>
                                                        </div>
                                                        <div className="text-end">
                                                            <span className="badge bg-danger mb-1">Unpaid</span>
                                                            <div className={`fw-bold${index === selectedJobIndex ? ' text-primary' : ''}`}>${job.amountDue.toFixed(2)}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        }

                        <div className="card p-4 mb-3">
                            <h3 className="mb-4 fw-bold"><i className="far fa-map-location-dot text-icon me-1"></i> Property & Area Information</h3>
                            <div className="row gx-5">
                                <div className="col-md">
                                    <div className="mb-2">
                                        <h3 className="mb-0">{customerName}</h3>
                                        <label className="light-label">contact name</label>
                                    </div>
                                    <div className="mb-2">
                                        <h3 className="mb-0">{propertyAddress}</h3>
                                        <label className="light-label">property</label>
                                    </div>
                                    {customerData.homePhone &&
                                        <div className="mb-2">
                                            <h3 className="mb-0">{customerData.homePhone}</h3>
                                            <label className="light-label">phone</label>
                                        </div>
                                    }
                                    {customerData.email &&
                                        <div>
                                            <h3 className="mb-0">{customerData.email}</h3>
                                            <label className="light-label">email</label>
                                        </div>
                                    }
                                </div>
                            </div>
                        </div>

                        <div className="card p-4 mb-3">
                            <h3 className="mb-4 fw-bold"><i className="far fa-credit-card text-icon me-1"></i> Payment Information</h3>

                            {customerData.hasSavedPaymentMethod &&
                                <div className="mb-4">
                                    <div className="d-flex gap-3">
                                        <div
                                            className={`card flex-fill p-3${!useNewCard ? ' border-primary shadow-sm' : ''}`}
                                            style={{cursor: 'pointer'}}
                                            onClick={() => setUseNewCard(false)}
                                        >
                                            <div className="d-flex align-items-center">
                                                <input type="radio" className="form-check-input me-3" checked={!useNewCard} onChange={() => setUseNewCard(false)}/>
                                                <div>
                                                    <div className="fw-bold">{customerData.savedCardType || 'Card'} ending in {customerData.savedCardLast4}</div>
                                                    <small className="text-muted">Exp {String(customerData.savedCardExpirationMonth).padStart(2, '0')}/{customerData.savedCardExpirationYear}</small>
                                                </div>
                                            </div>
                                        </div>
                                        <div
                                            className={`card flex-fill p-3${useNewCard ? ' border-primary shadow-sm' : ''}`}
                                            style={{cursor: 'pointer'}}
                                            onClick={() => setUseNewCard(true)}
                                        >
                                            <div className="d-flex align-items-center">
                                                <input type="radio" className="form-check-input me-3" checked={useNewCard} onChange={() => setUseNewCard(true)}/>
                                                <div>
                                                    <div className="fw-bold">Use a different card</div>
                                                    <small className="text-muted">Enter new card details below</small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            }

                            {useNewCard &&
                                <>
                                    <div className="row mb-4">
                                        <div className="col-lg-8 mb-3 mb-lg-0">
                                            <CustomInput label={'Card Number'} required name={'cardNumber'}
                                                         className={'form-control form-control-lg'} register={register}
                                                         errors={errors}
                                                         onChange={(e) => {
                                                             const raw = e.target.value;
                                                             const formatted = formatCardNumber(raw);
                                                             setValue("cardNumber", formatted);
                                                         }}
                                                         validation={{
                                                             validate: (value: string) =>
                                                                 validateCardNumber(value) || "Invalid card number"
                                                         }}/>
                                        </div>
                                        <div className="col-lg mb-3 mb-lg-0">
                                            <CustomInput label={'Exp'} required name={'cardExp'} onChange={handleExpDateChange}
                                                         className={'form-control form-control-lg'} register={register}
                                                         errors={errors}
                                                         validation={{
                                                             validate: (value: string) =>
                                                                 !value || /^(0[1-9]|1[0-2])\/\d{2}$/.test(value) || 'Format: MM/YY',
                                                         }}/>
                                        </div>
                                        <div className="col-lg">
                                            <CustomInput label={'CVV'} required name={'cardSecurity'}
                                                         className={'form-control form-control-lg'} register={register}
                                                         errors={errors} type={'number'}/>
                                        </div>
                                    </div>
                                    <div className="row mb-4">
                                        <div className="col-lg mb-3 mb-lg-0">
                                            <CustomInput label={'Name on Card'} required name={'cardName'}
                                                         className={'form-control form-control-lg'} register={register}
                                                         errors={errors}/>
                                        </div>
                                    </div>

                                    <div className="form-check mt-2">
                                        <input className="form-check-input" type="checkbox" id="sameAsProperty"
                                               checked={billingAddressSameAsProperty}
                                               onChange={() => setBillingAddressSameAsProperty(!billingAddressSameAsProperty)}/>
                                        <label className="form-check-label" htmlFor="sameAsProperty">
                                            Billing address same as property address
                                        </label>
                                    </div>

                                    {!billingAddressSameAsProperty &&
                                        <div className="mt-4">
                                            <div className="row mb-4">
                                                <div className="col-12">
                                                    <CustomInput label={'Street Address'} required name={'billingAddress'}
                                                                 className={'form-control form-control-lg'} register={register}
                                                                 errors={errors}/>
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-lg-6 mb-3 mb-lg-0">
                                                    <CustomInput label={'City'} required name={'billingCity'}
                                                                 className={'form-control form-control-lg'} register={register}
                                                                 errors={errors}/>
                                                </div>
                                                <div className="col-lg-3 mb-3 mb-lg-0">
                                                    <FormSelectCustom
                                                        label='State'
                                                        name="billingState"
                                                        className={'form-control form-control-lg'}
                                                        register={register}
                                                        options={allStates}
                                                        required
                                                    />
                                                </div>
                                                <div className="col-lg-3">
                                                    <CustomInput label={'Zip Code'} required name={'billingPostalCode'}
                                                                 className={'form-control form-control-lg'} register={register}
                                                                 errors={errors}/>
                                                </div>
                                            </div>
                                        </div>
                                    }

                                    {/*<div className="form-check mt-4">
                                        <input className="form-check-input" type="checkbox" id="saveCard"
                                               checked={saveCard}
                                               onChange={() => setSaveCard(!saveCard)}/>
                                        <label className="form-check-label" htmlFor="saveCard">
                                            Save this card for future payments
                                        </label>
                                    </div>*/}
                                </>
                            }
                        </div>
                    </div>
                    <div className="estimate-sidebar">
                        <div className="esimate-cost p-3">
                            <label>Amount Due</label>
                            <h2>${selectedJob.amountDue.toFixed(2)}</h2>
                        </div>
                        <hr/>
                        <div className="p-3">
                            <h3 className="mb-3">Summary</h3>
                            <div className="row mb-3 align-items-center justify-content-between">
                                <div className="col-auto">Sealcoating</div>
                                <div className="col-auto text-end">${selectedJob.sealcoatingPrice?.toFixed(2) ?? '0.00'}</div>
                            </div>
                            {(selectedJob.patchworkPrice ?? 0) > 0 &&
                                <div className="row mb-3 align-items-center justify-content-between">
                                    <div className="col-auto">Patchwork</div>
                                    <div className="col-auto text-end">${selectedJob.patchworkPrice.toFixed(2)}</div>
                                </div>
                            }
                            <div className="row align-items-center justify-content-between">
                                <div className="col-auto">Subtotal</div>
                                <div className="col-auto text-end">${((selectedJob.sealcoatingPrice ?? 0) + (selectedJob.patchworkPrice ?? 0)).toFixed(2)}</div>
                            </div>
                        </div>
                        <hr/>
                        {selectedJob.priceAdjustments?.map((adj, i) => (
                            <div className="row align-items-center justify-content-between px-3" key={i}>
                                <div className="col-auto">{adj.name}</div>
                                <div className="col-auto text-end text-success fw-bold">{adj.amount < 0 ? '-' : ''}${Math.abs(adj.amount).toFixed(2)}</div>
                            </div>
                        ))}
                        {(selectedJob.webDiscountAmount ?? 0) < 0 &&
                            <div className="row align-items-center justify-content-between px-3">
                                <div className="col-auto">Web Discount</div>
                                <div className="col-auto text-end text-success fw-bold">-${Math.abs(selectedJob.webDiscountAmount).toFixed(2)}</div>
                            </div>
                        }
                        {selectedJob.totalAmountReceived > 0 &&
                            <div className="row align-items-center justify-content-between px-3">
                                <div className="col-auto">Previously Paid</div>
                                <div className="col-auto text-end text-success fw-bold">-${selectedJob.totalAmountReceived.toFixed(2)}</div>
                            </div>
                        }
                        <hr/>
                        <div className="row align-items-center justify-content-between p-3 mb-5">
                            <div className="col-auto fw-bold">Total Due</div>
                            <div className="col-auto text-end text-primary fw-bold">${selectedJob.amountDue.toFixed(2)}</div>
                        </div>
                        <button disabled={submitting} onClick={handleSubmit} type="button"
                                className="btn btn-primary btn-lg w-100 fw-bold py-4 mb-5">
                            {submitting ? 'Processing...' : <>Send Payment <i className="fa-regular fa-arrow-right"></i></>}
                        </button>
                        <div className="row">
                            <div className="col-auto">
                                <i className="fas fa-question-circle fa-2x" style={{color: "#C1CBCF"}}></i>
                            </div>
                            <div className="col">
                                <p className="mb-0">Please contact Barreca with any questions about your payment or invoice.</p>
                            </div>
                        </div>
                    </div>
        </div>
    );
};

export default MakePaymentPage;
